import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, CalendarDays } from "lucide-react";
import PageHeader from "@/components/site/PageHeader";
import SectionEyebrow from "@/components/site/SectionEyebrow";
import ConsultationBand from "@/components/site/ConsultationBand";
import {
  AREAS_OF_EXPERTISE,
  SIGNATURE_WORKSHOPS,
  AUDIENCE_ROUTER,
} from "@/lib/content";

const VIEWS = {
  schools: {
    id: "schools",
    label: "Schools & Districts",
    eyebrow: "For school and district leaders",
    lead:
      "Five areas of focused work for districts, schools, and education leaders strengthening school climate, student belonging, safety, and the partnerships that sustain student success.",
    audienceTags: ["schools", "districts"],
  },
  organizations: {
    id: "organizations",
    label: "Organizations & Communities",
    eyebrow: "For organizations and community partners",
    lead:
      "For nonprofits, higher education partners, civic and corporate organizations, and community-serving teams working alongside schools and youth-serving ecosystems.",
    audienceTags: ["organizations", "community", "company"],
  },
};

function ServiceBlock({ area, idx, audience }) {
  const inquiryHref = `/inquiry?service=${encodeURIComponent(area.title)}&audience=${encodeURIComponent(audience)}`;
  return (
    <section
      id={area.id}
      data-testid={`service-block-${area.id}`}
      className="wt-section"
      style={idx % 2 === 1 ? { backgroundColor: "#FBF8F1" } : {}}
    >
      <div className="wt-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionEyebrow>{`Area 0${idx + 1}`}</SectionEyebrow>
            <h2 className="wt-h2 mt-4">{area.title}</h2>
            <p className="wt-body mt-5">{area.long}</p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link
                to={inquiryHref}
                data-testid={`service-block-inquiry-${area.id}`}
                className="wt-btn-primary"
              >
                Submit an Inquiry
                <ArrowUpRight size={16} aria-hidden="true" />
              </Link>
              <Link
                to="/book-consultation"
                data-testid={`service-block-book-${area.id}`}
                className="wt-btn-outline"
              >
                <CalendarDays size={14} aria-hidden="true" /> Book a Consultation
              </Link>
            </div>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 gap-4">
            <div className="wt-card p-5">
              <p className="wt-eyebrow">The problem</p>
              <p className="mt-2 wt-body">{area.problem}</p>
            </div>
            <div className="wt-card p-5">
              <p className="wt-eyebrow">What she builds</p>
              <p className="mt-2 wt-body">{area.builds}</p>
            </div>
            <div
              className="wt-card p-5"
              style={{ borderLeft: "3px solid var(--wt-primary)" }}
            >
              <p className="wt-eyebrow">The outcome</p>
              <p className="mt-2 wt-body">{area.outcome}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ServicesPage() {
  const [params, setParams] = useSearchParams();
  const view = params.get("view") === "organizations" ? "organizations" : "schools";
  const current = VIEWS[view];

  const visible = useMemo(
    () =>
      AREAS_OF_EXPERTISE.filter((a) =>
        a.audiences.some((tag) => current.audienceTags.includes(tag)),
      ),
    [current.audienceTags],
  );

  const setView = (next) => {
    const p = new URLSearchParams(params);
    p.set("view", next);
    setParams(p, { replace: true });
  };

  return (
    <div data-testid="services-page">
      <PageHeader
        eyebrow="Services"
        title="Two paths. The same standards. Different language and structure."
        lead="Wanakee organizes her work around the audience in the room. Switch the path below to see only what applies to you."
        testId="services-page-header"
      />

      {/* Path switcher */}
      <section className="pb-6">
        <div className="wt-container">
          <div
            role="tablist"
            aria-label="Service audience path"
            className="inline-flex flex-wrap items-center gap-2 p-1 rounded-full border"
            style={{ borderColor: "var(--wt-border)", backgroundColor: "#fff" }}
          >
            {AUDIENCE_ROUTER.map((opt) => {
              const active = opt.id === view;
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setView(opt.id)}
                  data-testid={`services-tab-${opt.id}`}
                  className="px-4 h-10 text-sm font-semibold rounded-full transition-colors"
                  style={{
                    backgroundColor: active ? "var(--wt-primary)" : "transparent",
                    color: active ? "var(--wt-bg)" : "var(--wt-text)",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <p className="mt-4 wt-text-muted text-sm max-w-2xl">{current.lead}</p>
        </div>
      </section>

      {/* Service blocks */}
      {visible.map((a, i) => (
        <ServiceBlock key={a.id} area={a} idx={i} audience={current.id} />
      ))}

      {/* Speaking section */}
      <section data-testid="services-speaking" className="wt-section" style={{ backgroundColor: "#FBF8F1" }}>
        <div className="wt-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <SectionEyebrow>Speaking &amp; Facilitation</SectionEyebrow>
              <h2 className="wt-h2 mt-4">Signature workshops and keynotes.</h2>
              <p className="wt-body mt-5">
                Wanakee is available for keynotes, workshops, professional development, panels,
                retreats, and youth experiences. Her sessions move rooms from buzzwords to
                practice and leave staff and students with a next step they can carry forward.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/book-speaking"
                  data-testid="services-speaking-cta"
                  className="wt-btn-primary"
                >
                  Book Me to Speak
                </Link>
                <Link
                  to="/book-consultation"
                  data-testid="services-speaking-consult"
                  className="wt-btn-outline"
                >
                  Book a Consultation
                </Link>
              </div>
            </div>
            <div className="lg:col-span-7">
              <p className="wt-eyebrow">Signature workshops &amp; program experience</p>
              <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                {SIGNATURE_WORKSHOPS.map((w, i) => (
                  <li
                    key={w}
                    data-testid={`signature-workshop-${i}`}
                    className="wt-card p-4 flex items-start gap-3"
                  >
                    <span
                      className="font-serif wt-primary text-base leading-none mt-1"
                      aria-hidden="true"
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-sm font-medium leading-snug">{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <ConsultationBand />
    </div>
  );
}
